import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { PageHero, SectionHead, Reveal } from "@/components/bits/Reveal";
import CollegeForm from "@/components/forms/CollegeForm";
import DriveGallery from "@/components/DriveGallery";
import { IMAGES, COLLEGE_PROCESS, INTERNSHIP_PROGRAMS } from "@/data/content";
import { useSEO } from "@/lib/seo";

const OFFERINGS = [
  { title: "Internship Programs", text: "Industrial and online internship formats matched to your students' courses and academic calendar." },
  { title: "Industrial Training", text: "Structured plant-based training for Diploma and ITI students with supervised, hands-on exposure." },
  { title: "Placement Support", text: "Placement opportunity pipelines connecting your graduates with our client network." },
  { title: "Recruitment Drives", text: "Coordinated drives — on campus or at client locations — connecting employers with your students." },
  { title: "Industry Exposure", text: "Manufacturing exposure programs and plant interactions that build industry awareness." },
  { title: "Candidate Registration", text: "Structured registration of student batches with profiles, preferences and readiness tracking." },
];

export default function Colleges() {
  useSEO(
    "College & TPO Partnerships | Maqline Solutions Pvt Ltd — Internships, Training & Placements",
    "Partner with Maqline Solutions Pvt Ltd for student internships, industrial training, placement support, recruitment drives and industry exposure programs for your institution."
  );
  const { hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 300);
    }
  }, [hash]);

  return (
    <main data-testid="colleges-page">
      <PageHero
        overline="For Colleges & TPOs"
        titleLines={["Bridging the Gap Between", "Education and Industry"]}
        description="Maqline Solutions works with educational institutions to create structured pathways for students through internships, industrial training, placement opportunities, recruitment drives and industry exposure."
        image={IMAGES.students}
      />

      <section className="bg-white py-16 sm:py-24" id="programs">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <SectionHead overline="What Your Institution Gets" title="Programs built for placement cells" description="Every engagement is planned with the TPO or faculty coordinator — around your courses, batch sizes and calendar." />
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-ink/10 border border-ink/10">
            {OFFERINGS.map((o, i) => (
              <Reveal key={o.title} delay={(i % 3) * 0.07} className="bg-white">
                <div className="p-8 h-full hover:bg-steel transition-colors duration-400" data-testid={`college-offering-${i}`}>
                  <span className="font-mono text-xs text-gold font-bold">0{i + 1}</span>
                  <h3 className="font-display text-xl text-ink mt-3 mb-2">{o.title}</h3>
                  <p className="text-sm text-muted leading-relaxed">{o.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <DriveGallery />

      <section className="bg-darknavy noise-bg py-16 sm:py-24" id="process">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <SectionHead dark overline="How Partnership Works" title="Six steps from enquiry to engagement" />
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {COLLEGE_PROCESS.map((s, i) => (
              <Reveal key={s.num} delay={i * 0.07}>
                <div className="border border-white/12 hover:border-gold/40 rounded-sm p-7 bg-white/[0.03] transition-colors duration-400 h-full" data-testid={`college-step-${s.num}`}>
                  <span className="font-mono text-2xl text-gold/80 font-bold">{s.num}</span>
                  <h3 className="font-display text-lg text-white mt-3 mb-2">{s.title}</h3>
                  <p className="text-[13px] text-white/55 leading-relaxed">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-steel py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-2 gap-14 items-start">
          <div>
            <SectionHead overline="Student Programs" title="What your students can access" />
            <div className="mt-10 space-y-4">
              {INTERNSHIP_PROGRAMS.map((p, i) => (
                <Reveal key={p.title} delay={i * 0.06}>
                  <div className="bg-white border-l-2 border-gold rounded-sm p-5" data-testid={`college-program-${i}`}>
                    <h3 className="font-display text-lg text-ink">{p.title}</h3>
                    <p className="text-sm text-muted mt-1">{p.text}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
          <div id="partner" className="scroll-mt-24">
            <SectionHead overline="Start the Conversation" title="Partnership enquiry" />
            <Reveal delay={0.15}>
              <div className="mt-8 border border-ink/10 rounded-sm p-6 sm:p-8 bg-white" data-testid="college-form-section">
                <CollegeForm />
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </main>
  );
}
